import { ChangeDetectorRef, Component, OnInit, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ApiService } from 'src/app/shared/services/http/api.service';
import { ToastrService } from 'src/app/shared/services/toastr.service';
import { ActivateManagementAccountComponent } from 'src/app/auth/signup-v2/steps/activate-management-account/activate-management-account.component';
import { DocumentUploadPopupComponent } from 'src/app/auth/signup-v2/steps/activate-management-account/document-upload-popup/document-upload-popup.component';
import { IUser } from 'src/app/interface/user.interface';

export const VERIFICATION_SNOOZE_KEY = 'pa_verification_snooze_until';

type SnoozeOption = 'tomorrow' | 'next3days' | 'next7days';

@Component({
  selector: 'pa-get-verified-popup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ActivateManagementAccountComponent,
    DocumentUploadPopupComponent,
  ],
  templateUrl: './get-verified-popup.component.html',
})
export class GetVerifiedPopupComponent implements OnInit {
  readonly visible = input(false);
  readonly closed = output<void>();
  /** When set, the popup shows and verifies this specific user instead of the logged-in user */
  readonly targetUserData = input<IUser | null>(null);

  private readonly auth = inject(AuthService);
  private readonly apiService = inject(ApiService);
  private readonly toastr = inject(ToastrService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly showNotifySheet = signal(false);
  readonly selectedNotifyOption = signal<SnoozeOption>('tomorrow');

  readonly notifyOptions: { label: string; value: SnoozeOption }[] = [
    { label: 'Tomorrow', value: 'tomorrow' },
    { label: 'Next 3 days', value: 'next3days' },
    { label: '7 days from now', value: 'next7days' },
  ];
  readonly showUploadDocsPopup = signal(false);
  readonly agreedToTerms = signal(false);
  readonly isSaving = signal(false);
  readonly paymentMethodSnoozed = signal(false);

  // Signals so the template re-renders immediately when profile is refreshed
  readonly profilePicture = signal('');
  readonly isDocsUploaded = signal(false);

  get currentUser() { return this.auth.getUser(); }
  /** The user whose data is displayed and verified — targetUserData() when set, otherwise logged-in user */
  get effectiveUser(): any { return this.targetUserData() ?? this.currentUser; }
  get userId() { return this.effectiveUser?.user_id || ''; }
  get userType() { return String(this.effectiveUser?.user_type || '2'); }

  get isResident() { return this.userType === '2'; }
  get isManagement() { return this.userType === '3'; }
  get isServiceProvider() { return this.userType === '4'; }
  get isContractor() { return this.userType === '5'; }

  get fullName(): string {
    const u = this.effectiveUser;
    if (!u) return '';
    if (u.user_type === '3') return u.management_name || '';
    if (u.user_type === '4') return u.service_provider_name || '';
    return `${u.first_name || ''} ${u.last_name || ''}`.trim();
  }

  get mobile() { return this.effectiveUser?.mobile || ''; }
  get email() { return this.effectiveUser?.email || ''; }

  get userSubTypeValue(): string {
    return String(this.effectiveUser?.user_sub_type || '3');
  }

  ngOnInit(): void {
    const u = this.effectiveUser;
    this.profilePicture.set(u?.profile_picture || '');
    const docsFlag = u?.all_docs_uploaded;
    this.isDocsUploaded.set(docsFlag === '1' || (docsFlag as any) === 1 || docsFlag === true);
    const termsFlag = u?.is_terms_accepted;
    this.agreedToTerms.set(termsFlag === '1' || (termsFlag as any) === 1 || termsFlag === true);
    // Always fetch fresh data — cached user may be stale
    this.refreshProfile();
  }

  onSubmit(): void {
    if (!this.agreedToTerms()) {
      this.toastr.showError('Please accept the terms to proceed.');
      return;
    }
    this.isSaving.set(true);
    this.apiService.httpPut(`user/verify/${this.userId}`, { is_terms_accepted: '1' }).subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (res.success) {
          this.refreshProfile();
          this.toastr.showSuccess('Verification submitted successfully!');
          this.closed.emit();
        } else {
          this.toastr.showError(res.message || 'Verification failed. Please try again.');
        }
      },
      error: () => {
        this.isSaving.set(false);
        this.toastr.showError('Verification failed. Please try again.');
      }
    });
  }

  onSkipForNow(): void {
    this.showNotifySheet.set(true);
  }

  onNotifySet(): void {
    const option = this.selectedNotifyOption();
    const now = new Date();
    const days = option === 'tomorrow' ? 1 : option === 'next3days' ? 3 : 7;
    now.setDate(now.getDate() + days);
    localStorage.setItem(VERIFICATION_SNOOZE_KEY, now.toISOString());
    this.showNotifySheet.set(false);
    this.paymentMethodSnoozed.set(true);
  }

  dismissNotifySheet(): void {
    this.showNotifySheet.set(false);
  }

  onActivationCompleted(): void {
    this.closed.emit();
  }

  onAddPaymentLater(): void {
    this.showNotifySheet.set(true);
  }

  onUploadDocsClose(): void {
    this.showUploadDocsPopup.set(false);
    this.refreshProfile();
  }

  private refreshProfile(): void {
    const isTargetUser = !!this.targetUserData();
    const endpoint = isTargetUser
      ? `profile?user_id=${this.userId}`
      : 'profile';
    this.apiService.httpGet(endpoint).subscribe({
      next: (res: any) => {
        if (res?.success && res?.data) {
          if (!isTargetUser) this.auth.updateData(res.data);
          const u = res.data;
          this.profilePicture.set(u?.profile_picture || '');
          const docsFlag = u?.all_docs_uploaded;
          this.isDocsUploaded.set(docsFlag === '1' || docsFlag === 1 || docsFlag === true);
          const termsFlag = u?.is_terms_accepted;
          this.agreedToTerms.set(termsFlag === '1' || termsFlag === 1 || termsFlag === true);
          this.cdr.detectChanges();
        }
      },
      error: () => {}
    });
  }

  static isVerificationSnoozed(): boolean {
    try {
      const raw = localStorage.getItem(VERIFICATION_SNOOZE_KEY);
      if (!raw) return false;
      return new Date(raw) > new Date();
    } catch {
      return false;
    }
  }
}
